import { Zap, Gauge, Thermometer, ShieldCheck } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
  hover: {
    y: -10,
    scale: 1.03,
  },
};

export default function SpecializedTuningServices() {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imageScale = useTransform(scrollYProgress, [0, 1], [0.95, 1.05]);

  const cards = [
    {
      title: "Stage 1",
      desc: "The perfect starting point for beginners. A conservative ECU remap paired with a stock performance air filter to safely unlock improved performance.",
      img: "/assets/perfomance/img3.jpg",
    },
    {
      title: "Stage 2",
      desc: "Includes Stage 1 plus a performance downpipe delivering 20–40% more power. Optional VP Racing additives improve octane levels.",
      img: "/assets/perfomance/img5.jpg",
    },
    {
      title:  "Stage 3",
      desc: "Complete performance build with hardware, cooling, braking, and turbo/supercharger systems for extreme enthusiasts.",
      img: "/assets/perfomance/img4.jpg",
    },
  ];

  return (
    <section ref={sectionRef} className="bg-[#0f0f0f] text-white py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold">
            AJP Performance Upgrades
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-gray-400 mt-3 text-sm">
            From ECU tuning to full builds, engineered for every performance level.
          </motion.p>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          className="grid md:grid-cols-3 gap-6 mb-20"
        >
          {cards.map((card, i) => (
            <motion.div
              key={i}
              variants={cardVariants}
              whileHover="hover"
              className="group relative h-full"
            >
              {/* Animated background glow */}
              <motion.div
                className="absolute -inset-0.5 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 opacity-0 blur-lg group-hover:opacity-60"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              <div className="relative flex flex-col h-full border border-gray-700 bg-[#151515] rounded-xl overflow-hidden">
                {/* Image */}
                <div className="overflow-hidden">
                  <img
                    src={card.img}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="font-semibold text-lg mb-2">
                    {card.title}
                  </h3>

                  <p className="text-sm text-gray-400 flex-grow">
                    {card.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom Section */}
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold">
              Built for Every Level
            </h3>

            <p className="text-gray-400 text-sm">
              Our staged upgrades ensure smooth progression from daily driving
              to extreme performance builds.
            </p>

            <ul className="grid grid-cols-2 gap-3 text-sm text-gray-300">
              {[
                "ECU Remapping",
                "Downpipe Upgrades",
                "Turbo Systems",
                "Cooling Enhancements",
              ].map((item, i) => (
                <motion.li
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="flex items-center gap-2"
                >
                  <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  {item}
                </motion.li>
              ))}
            </ul>

            <div className="grid grid-cols-2 gap-4 mt-6">
              {[Zap, Gauge, Thermometer, ShieldCheck].map((Icon, i) => (
                <motion.div
                  key={i}
                  whileHover={{ rotateY: 180 }}
                  transition={{ duration: 0.6 }}
                  className="relative h-20"
                >
                  <div className="absolute inset-0 flex items-center justify-center border border-gray-700 rounded-lg bg-[#151515]">
                    <Icon size={20} className="text-amber-400" />
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center border border-amber-500 rounded-lg bg-[#1a1a1a] rotateY-180">
                    <span className="text-xs">Performance Boost</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8 }}
            whileHover={{ scale: 1.05, rotate: 1 }}
            className="relative"
          >
            <motion.div
              className="absolute -inset-2 bg-gradient-to-r from-amber-500 to-orange-500 blur-2xl opacity-30 rounded-2xl"
              animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 3, repeat: Infinity }}
            />

            <img
              src="/assets/perfomance/img4.jpg"
              className="relative w-full h-[420px] object-cover rounded-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
